/**
 * WS-HF — which engine generates an image, as a PER-DEVICE preference.
 *
 * `"cloud"`: the existing path. The job runs on a cloud worker with the PLATFORM's provider
 * keys, and the platform is billed.
 *
 * `"higgsfield-mcp"`: the desktop app runs the generation itself, over MCP, on the OPERATOR'S
 * OWN Higgsfield subscription. The cloud still holds the job record, the input and the result,
 * so the queue, the candidates list and the export are unchanged — only who spends the money,
 * and whose machine makes the call, is different.
 *
 * Per device, like the render backend and the memory budgets, because the credential it
 * depends on is per device: a keychain entry on THIS Mac says nothing about the operator's
 * other machines.
 *
 * **The default is resolved, not stored.** `resolveGenerationSource` answers `"higgsfield-mcp"`
 * on a desktop that is CONNECTED and `"cloud"` everywhere else, so a first run needs no
 * migration and a disconnect falls back on its own. An explicit choice always wins, and an
 * explicit `"higgsfield-mcp"` on a disconnected device still resolves to `"cloud"` — a stored
 * preference must never strand a run on a credential that is gone.
 */

import type { CustomizationCapabilities } from "@genz/platform";
import { useSessionStore } from "./session.js";

const KEY = "genz.generationSource";

/**
 * WS-HF shared login — may THIS signed-in person generate on a per-computer Higgsfield sign-in
 * at all? Only a super-admin ("This computer only (advanced)" in Settings).
 *
 * The designers are the company's own team and every one of them generates on the ONE company
 * subscription a super-admin connects in Admin. An older build let anyone connect their own
 * account, so a designer's machine may still hold a sign-in — which, with the Settings section
 * now hidden from them, they could not even see to disconnect. So every place that picks the
 * device path passes `hfMcpAvailable && mayGenerateOnThisComputer()` as `available`, and a
 * designer's run goes to the cloud without the keychain being asked.
 */
export function mayGenerateOnThisComputer(): boolean {
  return useSessionStore.getState().isSuperAdmin();
}

export type GenerationSource = "cloud" | "higgsfield-mcp";

/** The operator's EXPLICIT choice, or `undefined` when they have never made one. */
export function getGenerationSourceChoice(): GenerationSource | undefined {
  try {
    const v = localStorage.getItem(KEY);
    return v === "cloud" || v === "higgsfield-mcp" ? v : undefined;
  } catch {
    return undefined;
  }
}

/** Record an explicit choice. `undefined` clears it, returning to the resolved default. */
export function setGenerationSourceChoice(v: GenerationSource | undefined): void {
  try {
    if (v === undefined) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, v);
  } catch {
    /* ignore (private mode / no storage) */
  }
}

/**
 * The source a run will ACTUALLY use.
 *
 * @param available whether this build has an MCP surface at all (`hfMcpAvailable()`), false on web
 * @param connected whether a Higgsfield account is signed in on this device
 */
export function resolveGenerationSource(
  available: boolean,
  connected: boolean,
  choice: GenerationSource | undefined = getGenerationSourceChoice(),
): GenerationSource {
  // On web there is no second path, whatever is stored. A device build that is not connected
  // has no credential to spend, so it is the same case.
  if (!available || !connected) return "cloud";
  return choice ?? "higgsfield-mcp";
}

/**
 * Is the company Higgsfield subscription connected on the cloud? Then it runs every Generate, a
 * super-admin's included (owner, 2026-09-26: "it should work for all"), and a per-computer
 * sign-in is only the fallback for when it is not.
 */
export function companySubscriptionReady(
  caps: Pick<CustomizationCapabilities, "shared_generation_source" | "shared_generation_connected"> | undefined,
): boolean {
  return caps?.shared_generation_source === "subscription" && caps.shared_generation_connected === true;
}

export type CapabilitiesReader = { customizationCapabilities?: () => Promise<CustomizationCapabilities> };
type Logger = { log: (level: string, message: string) => void };

/** `companySubscriptionReady` from the cloud's answer. A failed read keeps this computer's
 *  sign-in: it is the path that ran before this rule, and a status read is no reason to refuse. */
export async function readCompanySubscriptionReady(
  client: CapabilitiesReader,
  logger?: Logger,
): Promise<boolean> {
  try {
    return companySubscriptionReady(await client.customizationCapabilities?.());
  } catch (e) {
    logger?.log(
      "warn",
      "Generation source · could not read whether the company subscription is connected; " +
        `this computer's sign-in stays in use (${e instanceof Error ? e.message : String(e)})`,
    );
    return false;
  }
}

/** May a run use THIS computer's Higgsfield sign-in? A super-admin's desktop, and only while
 *  the company subscription is not connected. A designer's machine asks nothing. */
export async function thisComputerMayGenerate(
  mcp: { hfMcpAvailable: boolean },
  client: CapabilitiesReader,
  logger?: Logger,
): Promise<boolean> {
  return mcp.hfMcpAvailable && mayGenerateOnThisComputer()
    && !(await readCompanySubscriptionReady(client, logger));
}

/** The sentence a DESIGNER reads when no company generation source can run a job — in the case
 *  flow's photos step and, WS-AUTOKEY, in place of the customer page's "New template from
 *  photos" entry. A designer cannot fix it, so they are told who can. One string for both. */
export const DESIGNER_NOT_SET_UP_TEXT = "Image creation is not available yet. Ask your workspace admin to enable it.";
